import { useForm } from '@tanstack/react-form';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { AbortDowngradeModal } from '../-components/abort-downgrade-modal';
import { BuyCreditsModal } from '../-components/buy-credits-modal';
import { CancelPlanModal } from '../-components/cancel-plan-modal';
import { DowngradeImpossibleModal } from '../-components/downgrade-impossible-modal';
import { EditPlanModal } from '../-components/edit-plan-modal';
import { ManagePlanModal } from '../-components/manage-plan-modal';
import { ButtonSwitcher } from '../../../../../../components/button-switcher';
import { CreditUsage } from '../../../../../../components/credit-usage';
import { CurrentPlanCardExtended } from '../../../../../../components/current-plan-card-extended';
import { LimitBlock } from '../../../../../../components/limit-block';
import { BlymLoading } from '../../../../../../components/loading';
import { ModalSubscriptionSuccess } from '../../../../../../components/modal-subscription-success';
import { PlanCardExtended } from '../../../../../../components/plan-card-extended';
import { ToastVariant } from '../../../../../../components/toast';
import { ToastBanner, ToastBannerVariant } from '../../../../../../components/toast-banner';
import { getConfig } from '../../../../../../config';
import {
  type CancelReason,
  Currency,
  type Plan,
  PlanKind,
  PlanStatus,
  SubscriptionPaymentCycle,
} from '../../../../../../entity/api/organization-billing';
import { PlanFeature } from '../../../../../../entity/api/plan';
import { addTrackCrumb, trackError } from '../../../../../../error/tracking';
import { getLogger } from '../../../../../../logger';
import { captureException } from '../../../../../../logger/capture-exception';
import { buyExtraCredits } from '../../../../../../sdk/organization-billing';
import { getQueryClient } from '../../../../../../sdk/request';
import { requestBillingPortalUrl } from '../../../../../../sdk/subscription';
import {
  buildUserData,
  isGTMLoaded,
  storeCheckoutPlan,
  toPrice,
  toRenewalType,
} from '../../../../../../services/gtm';
import {
  getAuthUserFromCache,
  getOrganizationFromCache,
  useAbortDowngrade,
  useCancelSubscription,
  useOrganizationBilling,
  useOrganizationUsersList,
  useSubscribeOrganization,
  useSubscriptionPlans,
  useSyncBillingWithProvider,
  useUnCancelSubscription,
  useUpdateSubscription,
} from '../../../../../../services/storage';
import { tGlobal, tNamespace } from '../../../../../../services/translation';
import { useToast } from '../../../../../../shared/contexts/toast-context';
import { formatDateToDdMmYyyy } from '../../../../../../shared/date';
import { capitalize } from '../../../../../../shared/string';
import {
  SwitchKind,
  calculateMonthlyPrice,
  classifySwitch,
  getPlanFeatures,
  hasOrgFeature,
} from '../../../../../../shared/subscription';

const tBilling = tNamespace('app.billing.general');
const tSettings = tNamespace('app.settings.organization.billing');
const config = getConfig();

export const Route = createFileRoute(
  '/app/settings/_sidebar-settings-layout/organization/$id/_tabs-layout/plan'
)({
  beforeLoad: function () {
    addTrackCrumb({
      category: 'route',

      message: '/app/settings/_sidebar-settings-layout/organization/$id/_tabs-layout/plan',
      level: 'info',
    });
  },

  // eslint-disable-next-line no-use-before-define
  component: PlanTab,
});

export function PlanTab() {
  const { id } = Route.useParams() as unknown as { id: string };
  const navigate = useNavigate();
  const [updatePaymentMethodLoading, setUpdatePaymentMethodLoading] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [isCancelPlanModalOpen, setIsCancelPlanModalOpen] = useState(false);
  const [isManagePlanModalOpen, setIsManagePlanModalOpen] = useState(false);
  const [isEditPlanModalOpen, setIsEditPlanModalOpen] = useState(false);
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false);
  const [cancelPlanLoading, setCancelPlanLoading] = useState(false);
  const [currentPlanLoading, setCurrentPlanLoading] = useState(false);
  const [editPlanLoading, setEditPlanLoading] = useState(false);
  const [isSyncingPlan, setIsSyncingPlan] = useState(false);
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);
  const [isDowngradeSuccessModalOpen, setIsDowngradeSuccessModalOpen] = useState(false);
  const [downgradeSuccessDescription, setDowngradeSuccessDescription] = useState('');
  const [isAbortDowngradeModalOpen, setIsAbortDowngradeModalOpen] = useState(false);
  const [isDowngradeImpossibleModalOpen, setIsDowngradeImpossibleModalOpen] = useState(false);
  const [downgradeImpossibleUserCount, setDowngradeImpossibleUserCount] = useState(0);
  const [downgradeImpossiblePlanUserLimit, setDowngradeImpossiblePlanUserLimit] = useState(0);
  const [downgradeImpossiblePlanName, setDowngradeImpossiblePlanName] = useState('');
  const [selectedBillingCycle, setSelectedBillingCycle] = useState<SubscriptionPaymentCycle>(
    SubscriptionPaymentCycle.Year
  );
  const addToast = useToast();

  const queryClient = getQueryClient();
  // TanStack Query hooks
  const { data: orgBilling } = useOrganizationBilling(id);
  const org = getOrganizationFromCache(queryClient, id);
  const { data: plans = [] as Plan[] } = useSubscriptionPlans(id);

  const scaleYearlyPlan = plans.find(function (plan: Plan) {
    return plan.kind === PlanKind.Scale && plan.paymentCycle === SubscriptionPaymentCycle.Year;
  });
  const { data: orgUsers = [] } = useOrganizationUsersList(id);
  const currentUser = getAuthUserFromCache(queryClient);
  const userLanguage = currentUser?.language;

  // TanStack Query mutations
  const subscribeMutation = useSubscribeOrganization();
  const cancelMutation = useCancelSubscription();
  const unCancelMutation = useUnCancelSubscription();
  const updateSubscriptionMutation = useUpdateSubscription();
  const syncBillingMutation = useSyncBillingWithProvider();
  const abortDowngradeMutation = useAbortDowngrade();

  const isEditPlanDisabled =
    hasOrgFeature(orgBilling, PlanFeature.EnabledProducts) ||
    orgBilling?.features?.enabledProducts === true;

  function handleAbortDowngrade() {
    abortDowngradeMutation.mutate(id, {
      onSuccess: function () {
        // Sync with Stripe after releasing the schedule so the plan page reflects
        // PlanStatus.Subscribed immediately (ToastBanner and Cancel button disappear).
        setIsSyncingPlan(true);
        syncBillingMutation
          .mutateAsync(id)
          .catch(function (err) {
            captureException(
              err,
              {
                file: 'routes/app/settings/_sidebar-settings-layout/organization.$id/_tabs-layout/plan.tsx',
                method: 'handleAbortDowngrade/sync',
                infoMessage: 'unable to sync billing after abort downgrade',
              },
              trackError
            );
          })
          .finally(function () {
            setIsSyncingPlan(false);
            setIsAbortDowngradeModalOpen(false);
            addToast({
              title: tSettings('cancelDowngradeButton'),
              description: tSettings('cancelDowngradeInfo'),
              variant: ToastVariant.Success,
              id: Date.now(),
            });
          });
      },
      onError: function (err) {
        setIsAbortDowngradeModalOpen(false);
        addToast({
          title: tSettings('cancelDowngradeError'),
          description: tSettings('cancelDowngradeErrorDescription'),
          variant: ToastVariant.Error,
          id: Date.now(),
        });
        captureException(
          err,
          {
            file: 'routes/app/settings/_sidebar-settings-layout/organization.$id/_tabs-layout/plan.tsx',
            method: 'handleAbortDowngrade',
            infoMessage: 'unable to abort downgrade',
          },
          trackError
        );
      },
    });
  }

  function handleSubscribe(planId: string) {
    setLoadingPlanId(planId);
    subscribeMutation.mutate(
      {
        orgId: id,
        planId: planId,
        toltReferral: (window as { tolt_referral?: string | null })?.tolt_referral ?? null,
      },
      {
        onSuccess: function (data) {
          setLoadingPlanId(null);
          if (data.data.checkout != null && typeof data.data.checkout === 'string') {
            window.open(data.data.checkout);
          } else {
            addToast({
              title: tGlobal('somethingWentWrong'),
              description: 'Unable to redirect to checkout. Please try again.',
              variant: ToastVariant.Error,
              id: Date.now(),
            });
            captureException(
              new Error('Invalid checkout URL received from server'),
              {
                file: 'routes/app/settings/_sidebar-settings-layout/organization.$id/_tabs-layout/plan.tsx',
                method: 'handleSubscribe',
                infoMessage: 'checkout URL is null, undefined, or empty',
                extraData: { checkoutUrl: data.data.checkout },
              },
              trackError
            );
          }
        },
        onError: (err) => {
          setLoadingPlanId(null);
          addToast({
            title: tGlobal('somethingWentWrong'),
            variant: ToastVariant.Error,
            id: Date.now(),
          });
          captureException(
            err,
            {
              file: 'routes/app/settings/_sidebar-settings-layout/organization.$id/_tabs-layout/plan.tsx',
              method: 'handleSubscribe',
              infoMessage: 'unable to subscribe to plan',
            },
            trackError
          );
        },
      }
    );
  }

  const subscriptionForm = useForm({
    defaultValues: {
      plan: {
        id: plans?.[0]?.planId ?? '',
      },
    },
    onSubmit: () => {
      handleSubscribe(subscriptionForm.state.values.plan.id);
    },
  });

  function onUpdatePaymentMethodBtn(evt: MouseEvent) {
    if (evt != null && 'preventDefault' in evt) {
      evt.preventDefault();
      evt.stopPropagation();
    }

    setUpdatePaymentMethodLoading(true);

    requestBillingPortalUrl(id)
      .then(function (data) {
        setUpdatePaymentMethodLoading(false);
        window.open(data.url, '_blank');
      })
      .catch(function (err) {
        setUpdatePaymentMethodLoading(false);
        captureException(
          err,
          {
            file: '_tabs-layout/plan.tsx',
            method: 'onUpdatePaymentMethodBtn',
            infoMessage: 'unable to fetch billing portal',
          },
          trackError,
          addToast
        );
      });
  }

  function onInvoicesBtn(evt: MouseEvent) {
    if (evt != null && 'preventDefault' in evt) {
      evt.preventDefault();
      evt.stopPropagation();
    }

    setInvoicesLoading(true);

    requestBillingPortalUrl(id)
      .then(function (data) {
        setInvoicesLoading(false);
        window.open(data.url, '_blank');
      })
      .catch(function (err) {
        setInvoicesLoading(false);
        captureException(
          err,
          {
            file: '_tabs-layout/plan.tsx',
            method: 'onInvoicesBtn',
            infoMessage: 'unable to fetch billing portal',
          },
          trackError,
          addToast
        );
      });
  }

  function handleCancelPlan(reasons: CancelReason[], otherText?: string) {
    setCancelPlanLoading(true);
    cancelMutation
      .mutateAsync({
        orgId: id,
        feedback: reasons.join(', ') as CancelReason,
        cancelDetails: otherText ?? '',
      })
      .then(function (result) {
        setCancelPlanLoading(false);
        if (result.response.ok === true) {
          setIsCancelPlanModalOpen(false);
        }
      })
      .catch(function (err) {
        setCancelPlanLoading(false);
        setIsCancelPlanModalOpen(false);

        addToast({
          title: tBilling('cancelPlanFailed'),
          description: tBilling('cancelPlanFailedDescription'),
          variant: ToastVariant.Error,
          id: Date.now(),
        });

        captureException(
          err,
          {
            file: 'routes/app/settings/_sidebar-settings-layout/organization.$id/_tabs-layout/plan.tsx',
            method: 'handleCancelPlan',
            infoMessage: 'unable to cancel plan',
          },
          trackError
        );
      });
  }

  function onReactivatePlanBtn() {
    setCurrentPlanLoading(true);
    unCancelMutation.mutate(id, {
      onSuccess: () => {
        setCurrentPlanLoading(false);
      },
      onError: (err) => {
        setCurrentPlanLoading(false);
        addToast({
          title: tBilling('cancelPlanFailed'),
          description: tBilling('cancelPlanFailedDescription'),
          variant: ToastVariant.Error,
          id: Date.now(),
        });
        captureException(
          err,
          {
            file: 'routes/app/settings/_sidebar-settings-layout/organization.$id/_tabs-layout/plan.tsx',
            method: 'onReactivatePlanBtn',
            infoMessage: 'unable to reactivate plan',
          },
          trackError
        );
      },
    });
  }

  function handleEditPlanSelect(planId: string, _billingPeriod: SubscriptionPaymentCycle) {
    const targetPlan = plans.find(function (p) {
      return p.providerPlanId === planId || p.planId === planId;
    });

    if (
      targetPlan != null &&
      orgBilling?.plan?.kind != null &&
      orgBilling?.plan?.paymentCycle != null
    ) {
      const switchKind = classifySwitch(
        {
          kind: orgBilling.plan.kind as PlanKind,
          paymentCycle: orgBilling.plan.paymentCycle as SubscriptionPaymentCycle,
        },
        {
          kind: targetPlan.kind as PlanKind,
          paymentCycle: targetPlan.paymentCycle as SubscriptionPaymentCycle,
        }
      );

      if (switchKind === SwitchKind.Downgrade && orgUsers.length > targetPlan.maxTeamUserCount) {
        setDowngradeImpossibleUserCount(orgUsers.length);
        setDowngradeImpossiblePlanUserLimit(targetPlan.maxTeamUserCount);
        setDowngradeImpossiblePlanName(targetPlan.kind);
        setIsEditPlanModalOpen(false);
        setIsDowngradeImpossibleModalOpen(true);
        return;
      }
    }

    setEditPlanLoading(true);

    updateSubscriptionMutation.mutate(
      { orgId: id, planId },
      {
        onSuccess: function (data) {
          setEditPlanLoading(false);

          if (isGTMLoaded() && targetPlan != null) {
            window.dataLayer.push({
              event: 'change_plan',
              // eslint-disable-next-line camelcase
              old_plan: orgBilling?.plan?.kind,
              // eslint-disable-next-line camelcase
              new_plan: targetPlan.kind,
              // eslint-disable-next-line camelcase
              renewal_type: toRenewalType(targetPlan.paymentCycle),
              price: toPrice(targetPlan.pricePerMonth),
              value: toPrice(
                targetPlan.paymentCycle === SubscriptionPaymentCycle.Year
                  ? targetPlan.pricePerMonth * 12
                  : targetPlan.pricePerMonth
              ),
              currency: orgBilling?.currentCurrency?.toUpperCase(),
              // eslint-disable-next-line camelcase
              user_id: currentUser?._id,
              organizationId: id,
              // eslint-disable-next-line camelcase
              user_data: currentUser != null ? buildUserData(currentUser) : undefined,
            });
          }

          if (data.redirectUrl != null) {
            window.location.href = data.redirectUrl;
          } else if (data.immediate) {
            // The plan swap happened synchronously on the API, but the billing
            // record may not yet reflect it on the frontend (stale cache / async
            // Stripe webhook). Show a full-screen loader while we pull the
            // authoritative state from Stripe via the sync endpoint.
            setIsEditPlanModalOpen(false);
            setIsSyncingPlan(true);
            syncBillingMutation
              .mutateAsync(id)
              .catch(function (err) {
                captureException(
                  err,
                  {
                    file: 'routes/app/settings/_sidebar-settings-layout/organization.$id/_tabs-layout/plan.tsx',
                    method: 'handleEditPlanSelect/sync',
                    infoMessage: 'unable to sync billing after plan switch',
                  },
                  trackError
                );
              })
              .finally(function () {
                setIsSyncingPlan(false);
                addToast({
                  title: tBilling('switchedPlan'),
                  variant: ToastVariant.Success,
                  id: Date.now(),
                });
              });
          } else if (data.effectiveAt != null) {
            setIsEditPlanModalOpen(false);
            // Sync with Stripe before showing the modal so the plan page reflects
            // PlanStatus.Switched (ToastBanner + Cancel downgrade button) immediately.
            // The Stripe schedule is already created by this point, so the sync will
            // pick it up and set currentPlanStatus = Switched in the cache.
            const selectedPlan = plans.find(function (p) {
              return p.providerPlanId === planId || p.planId === planId;
            });
            const { effectiveAt } = data;
            setIsSyncingPlan(true);
            syncBillingMutation
              .mutateAsync(id)
              .catch(function (err) {
                captureException(
                  err,
                  {
                    file: 'routes/app/settings/_sidebar-settings-layout/organization.$id/_tabs-layout/plan.tsx',
                    method: 'handleEditPlanSelect/sync-downgrade',
                    infoMessage: 'unable to sync billing after downgrade',
                  },
                  trackError
                );
              })
              .finally(function () {
                setIsSyncingPlan(false);
                setDowngradeSuccessDescription(
                  tBilling('subscriptionUpdatedDescription', {
                    plan: capitalize(selectedPlan?.kind as string),
                    date: formatDateToDdMmYyyy(new Date(effectiveAt)),
                  })
                );
                setIsDowngradeSuccessModalOpen(true);
              });
          }
        },
        onError: (err) => {
          setEditPlanLoading(false);
          setIsEditPlanModalOpen(false);
          addToast({
            title: tGlobal('somethingWentWrong'),
            variant: ToastVariant.Error,
            id: Date.now(),
          });
          captureException(
            err,
            {
              file: 'routes/app/settings/_sidebar-settings-layout/organization.$id/_tabs-layout/plan.tsx',
              method: 'handleEditPlanSelect',
              infoMessage: 'unable to update subscription',
            },
            trackError
          );
        },
      }
    );
  }

  // TODO: Handle it when the API is ready, it's featured flagged for now.
  // function handleBuyCredits(credits: number, totalPrice: number) {
  //   setIsBuyCreditsModalOpen(false);
  //   addToast({
  //     title: tBilling('creditsPurchased'),
  //     description: tBilling('creditsPurchasedDescription', { count: credits }),
  //     variant: ToastVariant.Success,
  //     id: Date.now(),
  //   });
  // }

  // Reset form when plans are loaded
  useEffect(
    function () {
      if (plans.length > 0) {
        subscriptionForm.reset({
          plan: {
            id: plans?.[0]?.planId ?? '',
          },
        });
      }
    },
    [plans, subscriptionForm]
  );

  if (orgBilling?.currentPlanName == null && orgBilling?.plan == null && plans.length === 0) {
    return null;
  }

  return (
    <div className="mt-8 flex flex-col gap-10">
      {isSyncingPlan ? (
        <div className="pointer-events-none fixed inset-0 z-50 flex h-screen w-screen flex-col items-center justify-center bg-white transition-opacity duration-500 ease-out">
          <BlymLoading />
        </div>
      ) : null}
      {orgBilling?.currentPlanStatus === PlanStatus.NotSubscribed ||
      orgBilling?.currentPlanStatus === PlanStatus.Cancelled ? (
        <div className="flex flex-col gap-6">
          <div className="mx-auto text-center">
            <div className="relative">
              <img
                src="/sparkles.png"
                alt="Sparkles"
                width={45}
                height={45}
                className="absolute -left-10 top-2 -translate-y-full"
              />
              <img
                src="/single-sparkle.png"
                alt="Sparkles"
                width={24}
                height={24}
                className="absolute bottom-4 right-4 translate-y-1/2 translate-y-full"
              />
              <p className="font-display-2 text-center">
                <Trans
                  i18nKey={
                    orgBilling?.currentPlanStatus === PlanStatus.Cancelled
                      ? 'app.billing.general.reactivatePlanTitle'
                      : 'app.billing.general.chooseYourPlan'
                  }
                  values={{ trialDays: config.trialDays }}
                  components={{ br: <br />, span: <span /> }}
                />
              </p>
            </div>
          </div>
          <p className="font-body-2-sb mb-8 text-center text-branding-accent-900">
            {tBilling('fullAccess')}
          </p>

          {/* Billing Cycle Toggle */}
          <div className="mb-8 flex justify-center md:justify-end">
            <ButtonSwitcher
              options={[
                { value: SubscriptionPaymentCycle.Month, label: tBilling('monthly') },
                {
                  value: SubscriptionPaymentCycle.Year,
                  label: tBilling('yearlyWithDiscount', {
                    percent: scaleYearlyPlan?.priceDiscount ?? 0,
                  }),
                },
              ]}
              value={selectedBillingCycle}
              onChange={function (value) {
                setSelectedBillingCycle(value as SubscriptionPaymentCycle);
              }}
            />
          </div>

          <form className="mb-13">
            <subscriptionForm.Field
              name="plan"
              // eslint-disable-next-line react/no-children-prop
              children={function () {
                const filteredPlans: Plan[] = plans.filter(function (plan: Plan) {
                  return plan.paymentCycle === selectedBillingCycle;
                });

                function getCtaText(plan: Plan) {
                  if (orgBilling?.currentPlanStatus === PlanStatus.Cancelled) {
                    const isCurrentPlan = orgBilling?.plan?.providerPlanId === plan.providerPlanId;
                    return isCurrentPlan ? tBilling('reactivatePlan') : tBilling('choosePlan');
                  }

                  return tBilling('startTrial', { trialDays: config.trialDays });
                }

                return (
                  <div className="mb-12 flex flex-col items-start gap-6 md:flex-row">
                    {filteredPlans.map(function (plan: Plan) {
                      const isBestValue = plan.kind === PlanKind.Scale;

                      return (
                        <PlanCardExtended
                          key={plan.providerPlanId}
                          planId={plan.providerPlanId}
                          title={plan.kind}
                          originalPrice={plan.pricePerMonth}
                          currentPrice={plan.pricePerMonth}
                          discountPercentage={plan.priceDiscount}
                          currency={plan.currency}
                          isBestValue={isBestValue}
                          paymentCycle={plan.paymentCycle}
                          ctaText={getCtaText(plan)}
                          ctaLoading={loadingPlanId === plan.providerPlanId}
                          onCtaClick={function () {
                            storeCheckoutPlan({
                              plan: plan.kind,
                              pricePerMonth: plan.pricePerMonth,
                              priceDiscount: plan.priceDiscount,
                              paymentCycle: plan.paymentCycle,
                              currency: plan.currency,
                            });
                            handleSubscribe(plan.providerPlanId);

                            if (isGTMLoaded()) {
                              window.dataLayer.push({
                                event: 'checkout',
                                plan: plan.kind,
                                // eslint-disable-next-line camelcase
                                renewal_type: toRenewalType(plan.paymentCycle),
                                price: toPrice(plan.pricePerMonth),
                                value: toPrice(
                                  plan.paymentCycle === SubscriptionPaymentCycle.Year
                                    ? plan.pricePerMonth * 12
                                    : plan.pricePerMonth
                                ),
                                currency: plan.currency.toUpperCase(),
                                // eslint-disable-next-line camelcase
                                user_id: currentUser?._id,
                                organizationId: id,
                                // eslint-disable-next-line camelcase
                                user_data:
                                  currentUser != null ? buildUserData(currentUser) : undefined,
                              });
                            } else {
                              getLogger().warn('data layer is not initialized');
                              captureException(
                                new Error('data layer is not initialized'),
                                {
                                  file: 'routes/app/settings/_sidebar-settings-layout/organization.$id/_tabs-layout/plan.tsx',
                                  method: 'onCtaClick',
                                  infoMessage: 'data layer is not initialized',
                                  extraData: {
                                    plan: plan.kind,
                                    price: calculateMonthlyPrice(
                                      plan.pricePerMonth,
                                      plan.priceDiscount
                                    ),
                                    currency: plan.currency,
                                  },
                                },
                                trackError
                              );
                            }
                          }}
                          features={getPlanFeatures(plan)}
                          className={twMerge(isBestValue ? 'md:translate-y-[-16px]' : '', 'flex-1')}
                        />
                      );
                    })}
                  </div>
                );
              }}
            />
            {orgBilling?.currentPlanStatus === PlanStatus.Cancelled ? null : (
              <div className="flex flex-col items-center gap-4">
                <a
                  href={config.urlWebsitePriceComparison}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-body-2-sb text-branding-accent underline hover:text-branding-accent-800"
                >
                  {tBilling('seeFullFeatureComparison')} →
                </a>
              </div>
            )}
          </form>
        </div>
      ) : null}

      {orgBilling?.currentPlanStatus === PlanStatus.Trialing ||
      orgBilling?.currentPlanStatus === PlanStatus.Subscribed ||
      orgBilling?.currentPlanStatus === PlanStatus.Switched ||
      orgBilling?.currentPlanStatus === PlanStatus.PastDue ||
      orgBilling?.currentPlanStatus === PlanStatus.Cancelling ? (
        <div>
          {orgBilling?.currentPlanStatus === PlanStatus.PastDue ? (
            <ToastBanner
              title={tBilling('paymentFailed')}
              description={tBilling('paymentFailedDescription')}
              variant={ToastBannerVariant.Error}
              className="mb-13"
              cta={{
                text: tBilling('updatePaymentMethod'),
                onClick: () => onUpdatePaymentMethodBtn(new MouseEvent('click')),
                loading: updatePaymentMethodLoading,
              }}
            />
          ) : null}
          {orgBilling?.currentPlanStatus === PlanStatus.Switched ? (
            <ToastBanner
              title={tBilling('switchedPlan')}
              description={tBilling('switchedPlanDescription', {
                date: formatDateToDdMmYyyy(new Date(orgBilling?.currentPlanEndDate ?? 0)),
              })}
              variant={ToastBannerVariant.Success}
              className="mb-13"
              cta={{
                text: tSettings('cancelDowngradeButton'),
                onClick: () => setIsAbortDowngradeModalOpen(true),
                loading: abortDowngradeMutation.isPending,
              }}
            />
          ) : null}
          <div className="flex flex-col gap-6 md:flex-row">
            <CurrentPlanCardExtended
              organizationBilling={orgBilling}
              userLanguage={userLanguage}
              onPaymentMethodClick={() => void onUpdatePaymentMethodBtn(new MouseEvent('click'))}
              onInvoicesClick={() => void onInvoicesBtn(new MouseEvent('click'))}
              onManagePlanClick={() => setIsManagePlanModalOpen(true)}
              onUncancelPlanClick={() => onReactivatePlanBtn()}
              onAbortDowngradeClick={() => setIsAbortDowngradeModalOpen(true)}
              paymentMethodLoading={updatePaymentMethodLoading}
              invoicesLoading={invoicesLoading}
              uncancelPlanLoading={currentPlanLoading}
            />
          </div>

          <div className="mt-10 flex w-full">
            <CreditUsage
              organizationBilling={orgBilling}
              categoryLabel={tBilling('contentCreationAndRewrite')}
              onBuyCredits={() => setIsBuyCreditsModalOpen(true)}
              className="w-full"
            />
          </div>

          {orgBilling?.plan?.maxTeamUserCount != null ? (
            <div className="mt-10 flex w-full md:w-1/2">
              <LimitBlock
                label={tBilling('teamMembers')}
                current={orgUsers.length}
                total={orgBilling.plan.maxTeamUserCount}
                isUnlimited={getConfig().plans[orgBilling.plan.kind].areUsersUnlimited}
                onUpgradeClick={() => setIsManagePlanModalOpen(true)}
                className="w-full"
              />
            </div>
          ) : null}

          <CancelPlanModal
            open={isCancelPlanModalOpen}
            onOpenChange={setIsCancelPlanModalOpen}
            accessUntilDate={orgBilling?.currentPlanEndDate ?? 0}
            isTrialing={orgBilling?.currentPlanStatus === PlanStatus.Trialing}
            onConfirmCancellation={handleCancelPlan}
            onKeepPlan={() => setIsCancelPlanModalOpen(false)}
            loading={cancelPlanLoading}
          />
          <ManagePlanModal
            open={isManagePlanModalOpen}
            onOpenChange={setIsManagePlanModalOpen}
            planName={orgBilling?.plan?.kind ?? ''}
            billingCycle={orgBilling?.plan?.paymentCycle ?? SubscriptionPaymentCycle.Month}
            renewsOnDate={orgBilling?.currentPlanEndDate ?? 0}
            monthlyPrice={orgBilling?.plan?.pricePerMonth ?? 0}
            yearlyPrice={
              orgBilling?.plan?.paymentCycle === SubscriptionPaymentCycle.Year
                ? (orgBilling?.plan?.pricePerMonth ?? 0) * 12
                : undefined
            }
            isEditPlanDisabled={isEditPlanDisabled}
            currency={orgBilling?.currentCurrency ?? Currency.Usd}
            discountPercent={orgBilling?.plan?.priceDiscount}
            userLanguage={userLanguage}
            onEditPlan={function () {
              setIsManagePlanModalOpen(false);
              setIsEditPlanModalOpen(true);
            }}
            onCancelPlan={function () {
              setIsManagePlanModalOpen(false);
              setIsCancelPlanModalOpen(true);
            }}
          />
          <EditPlanModal
            open={isEditPlanModalOpen}
            onOpenChange={setIsEditPlanModalOpen}
            currency={orgBilling?.currentCurrency ?? Currency.Usd}
            plans={plans}
            discountPercent={orgBilling?.plan?.priceDiscount ?? 0}
            userLanguage={userLanguage}
            currentPlanId={orgBilling?.plan?.providerPlanId}
            currentPlanKind={orgBilling?.plan?.kind as PlanKind | undefined}
            currentPaymentCycle={
              orgBilling?.plan?.paymentCycle as SubscriptionPaymentCycle | undefined
            }
            currentPlanEndDate={orgBilling?.currentPlanEndDate}
            onPlanSelect={handleEditPlanSelect}
            upgradeLoading={editPlanLoading}
            isTrialing={orgBilling?.currentPlanStatus === PlanStatus.Trialing}
          />
          <BuyCreditsModal
            open={isBuyCreditsModalOpen}
            onOpenChange={setIsBuyCreditsModalOpen}
            pricePerCredit={orgBilling?.plan?.extraCreditPrice ?? 0}
            currency={orgBilling?.currentCurrency ?? Currency.Usd}
            organizationName={org?.name ?? ''}
            onBuyCredits={async function (credits) {
              setIsBuyCreditsModalOpen(false);
              try {
                const result = await buyExtraCredits(id, credits);
                if (result.data.checkout != null && typeof result.data.checkout === 'string') {
                  window.location.href = result.data.checkout;
                } else {
                  addToast({
                    title: tGlobal('somethingWentWrong'),
                    description: 'Unable to redirect to checkout. Please try again.',
                    variant: ToastVariant.Error,
                    id: Date.now(),
                  });
                  captureException(
                    new Error('Invalid checkout URL received from server'),
                    {
                      file: 'routes/app/settings/_sidebar-settings-layout/organization.$id/_tabs-layout/plan.tsx',
                      method: 'onBuyCredits',
                      infoMessage: 'checkout URL is null, undefined, or empty',
                      extraData: { checkoutUrl: result.data.checkout },
                    },
                    trackError
                  );
                }
              } catch (err) {
                addToast({
                  title: tGlobal('somethingWentWrong'),
                  variant: ToastVariant.Error,
                  id: Date.now(),
                });
                captureException(
                  err,
                  {
                    file: 'routes/app/settings/_sidebar-settings-layout/organization.$id/_tabs-layout/plan.tsx',
                    method: 'onBuyCredits',
                    infoMessage: 'error while creating extra credits checkout',
                  },
                  trackError
                );
              }
            }}
          />
          <ModalSubscriptionSuccess
            open={isDowngradeSuccessModalOpen}
            onOpenChange={setIsDowngradeSuccessModalOpen}
            title={tBilling('switchedPlan')}
            description={downgradeSuccessDescription}
          />
          <AbortDowngradeModal
            open={isAbortDowngradeModalOpen}
            onOpenChange={setIsAbortDowngradeModalOpen}
            onConfirm={handleAbortDowngrade}
            onGoBack={() => setIsAbortDowngradeModalOpen(false)}
            loading={abortDowngradeMutation.isPending}
          />
          <DowngradeImpossibleModal
            open={isDowngradeImpossibleModalOpen}
            onOpenChange={setIsDowngradeImpossibleModalOpen}
            currentUserCount={downgradeImpossibleUserCount}
            planUserLimit={downgradeImpossiblePlanUserLimit}
            planName={downgradeImpossiblePlanName}
            onManageMembers={function () {
              setIsDowngradeImpossibleModalOpen(false);
              void navigate({
                to: `/app/settings/organization/${id}/users` as never,
              });
            }}
          />
        </div>
      ) : null}
    </div>
  );
}
